/**
 * v3.4.7 — <ClarityScoreEmbed>
 * ============================================================================
 * Self-contained Clarity Score card for use outside Insights.
 *
 * Phase 1 use case: relocated from Insights → Patterns tab to
 * Settings → Life Profile per product decision. The score reflects the
 * user's overall financial health, which fits Life Profile better than
 * a spending-pattern surface.
 *
 * Wraps `useInsightsData` + `computeClarityScore` internally so the
 * consumer just drops <ClarityScoreEmbed /> and gets the whole thing.
 */
import React, { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useCurrencyContext } from '@/hooks/useCurrencyContext';
import { useInsightsData } from '@/hooks/useInsightsData';
import { computeClarityScore } from '@/lib/clarityScore';

export default function ClarityScoreEmbed() {
  const { profile, user } = useAuth();
  const currencyCtx = useCurrencyContext();
  const { txns, goals, recurring, onboarding, holdings, liabilities, loading } =
    useInsightsData(user?.id, currencyCtx);

  const score = useMemo(
    () => computeClarityScore({ txns, onboarding, holdings, liabilities, goals, recurring, profile }),
    [txns, onboarding, holdings, liabilities, goals, recurring, profile],
  );

  if (loading) {
    return <div className="rounded-2xl bg-evergreen/80 h-40 animate-pulse" data-testid="clarity-score-loading" />;
  }

  const {
    overall = 0,
    buckets = [],
    band = '—',
    deltaPts = 0,
    stage = 'established',
    confidence = 'high',
  } = score || {};
  const muted = stage !== 'established';
  const stageLabel = stage === 'none' ? 'Building your clarity'
                   : stage === 'early' ? 'Early estimate'
                   : null;

  return (
    <div className="bg-evergreen rounded-2xl p-6 text-white" data-testid="clarity-score-embed">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40">Clarity Score</p>
        {stageLabel && (
          <span
            className="text-[10px] font-semibold tracking-widest uppercase px-2 py-1 rounded-full bg-white/10 text-white/60"
            data-testid="clarity-score-stage"
            title={`Confidence: ${confidence}`}
          >
            {stageLabel}
          </span>
        )}
      </div>
      <div className="flex items-end gap-6 flex-wrap">
        <div>
          <p
            className={`font-fraunces text-6xl font-medium leading-none tracking-tight num ${muted ? 'text-white/50' : ''}`}
            data-testid="clarity-score-value"
          >
            {stage === 'none' ? '—' : overall}
          </p>
          <p className="text-sage-light text-sm mt-2 font-medium" data-testid="clarity-score-band">{band}</p>
          {deltaPts !== 0 && stage === 'established' && (
            <p className="text-white/40 text-xs mt-1">
              {deltaPts >= 0 ? 'Up' : 'Down'} {Math.abs(deltaPts)} point{Math.abs(deltaPts) === 1 ? '' : 's'} vs last month
            </p>
          )}
        </div>
        <div className="flex-1 min-w-[220px] space-y-3">
          {buckets.map((d) => (
            <div key={d.label} data-testid={`clarity-score-bucket-${d.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/50 text-[11px]">{d.label}</span>
                <span className="text-white/70 text-[11px] font-medium num">{d.score}</span>
              </div>
              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${d.score}%`, backgroundColor: d.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
